import { List, useTable } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { Company } from '@viq-admin/types';
import { restoreCompany } from '../../api/companies';
import {
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — an inline literal here would bust
 * DataTable's `effective` memo (and, through it, `prepared`/`finalColumns`)
 * on every render for no reason. See DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
};

/**
 * All-tenant list of archived companies (the `?archived=true` view driven by
 * `meta.archived`).
 *
 * Row click opens the same `/companies/:id` detail page as the active list —
 * CompanyDetail is archived-aware and swaps Archive for Restore in its header
 * (see companies/CompanyDetail.tsx). Restore also lives in the row kebab for speed.
 *
 * The previous implementation rendered a live Edit button here, gated by
 * nothing, because RowActions only gated its destructive slot.
 */
export function CompanyArchivedList() {
  const { tableProps } = useTable<Company>({
    resource: 'companies',
    meta: { archived: true },
    pagination: { mode: 'off' },
  });
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  const columns: DataColumn<Company>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 260 },
    { dataIndex: 'entraHint', key: 'entraHint', title: 'Entra Hint', kind: 'text', width: 220 },
    { dataIndex: 'archivedAt', key: 'archivedAt', title: 'Archived', kind: 'date' },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: Company) => (
        <RowActionsMenu
          actions={[
            {
              key: 'restore',
              label: 'Restore',
              cap: 'manage:Company',
              onRun: async () => {
                await restoreCompany(record.id);
                invalidate({ resource: 'companies', invalidates: ['list'] });
              },
              confirm: {
                title: `Restore ${record.name}?`,
                content: 'This company will be able to access the platform again.',
                okText: 'Restore',
                successMessage: `Restored ${record.name}.`,
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <List headerButtons={() => <Link to="/companies">← Back to active</Link>}>
      <DataTable<Company>
        tableProps={tableProps}
        columns={columns}
        rowKey="id"
        storageKey="companiesArchivedList"
        state={state}
        onStateChange={setState}
        defaultState={DEFAULT_STATE}
        searchFields={['name', 'entraHint']}
        searchPlaceholder="Search archived companies"
        onRowClick={(record) => navigate(`/companies/${record.id}`)}
        empty={{ title: 'No archived companies' }}
      />
    </List>
  );
}
